// Shared presentational piece for the editable payment-request covering
// letter (migration 029) — used by both pages/ApprovalDetailPage.jsx's
// PaymentSection (admin) and pages/SalesQuotationDetailPage.jsx's
// payment-letter block (sales). The two pages fetch `payment` through
// different hooks (admin's useQuotationPayment vs sales's own of the same
// name in hooks/useSalesQuotations.js) and call a different PATCH route, so
// only this form itself — not the data-fetching — is shared: textarea bound
// to letter_body, a Save button, and the "locked once paid" note.
//
// `onSave(letterBody)` is called with the current textarea value on Save;
// the caller owns the mutation, the toast, and the 409 PAYMENT_REQUEST_LOCKED
// handling (both calling sections pass ['PAYMENT_REQUEST_LOCKED', ...] to
// their own toastErr). `readOnlyPaid` disables the textarea and the button
// for real — not just a visual dim — so a race with a concurrent mark-paid
// still can't produce an edit the user believes went through.
//
// Local text state re-syncs from `payment.letter_body` only when the
// payment's identity or its own letter_updated_at changes (not on every
// parent re-render, which would stomp an in-progress edit the instant an
// unrelated field, e.g. amount_due, happened to refetch) — same
// "initializedFor" guard shape as WorkspacePage.jsx/ApprovalDetailPage.jsx's
// own terms-prefill effects.

import { useEffect, useRef, useState } from 'react';
import { useLanguage } from '../context/LanguageContext.jsx';
import { Spinner } from 'shared/components/Feedback/index.js';
import styles from './PaymentLetterForm.module.css';

export default function PaymentLetterForm({ payment, onSave, isSaving, readOnlyPaid }) {
  const { t } = useLanguage();
  const syncKey = `${payment.id}:${payment.letter_updated_at || ''}`;
  const syncedForRef = useRef(null);
  const [letterBody, setLetterBody] = useState(payment.letter_body || '');

  useEffect(() => {
    if (syncedForRef.current === syncKey) return;
    setLetterBody(payment.letter_body || '');
    syncedForRef.current = syncKey;
  }, [syncKey, payment.letter_body]);

  const locked = Boolean(readOnlyPaid);
  const dirty = letterBody !== (payment.letter_body || '');
  const canSave = !locked && dirty && !isSaving;

  const handleSave = () => {
    if (!canSave) return;
    onSave(letterBody);
  };

  return (
    <div className={styles.wrap}>
      <label className={styles.field}>
        {t('paymentLetter.label')}
        <textarea
          className={styles.textarea}
          rows={8}
          maxLength={10000}
          value={letterBody}
          onChange={(e) => setLetterBody(e.target.value)}
          disabled={locked}
        />
      </label>
      {locked && <p className={styles.lockedNote}>{t('paymentLetter.lockedNote')}</p>}
      <div className={styles.actions}>
        <button
          type="button"
          className={styles.saveButton}
          onClick={handleSave}
          disabled={!canSave}
        >
          {isSaving && <Spinner />} {isSaving ? t('paymentLetter.saving') : t('paymentLetter.save')}
        </button>
      </div>
    </div>
  );
}
